import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { brandingAPI, companiesAPI } from '../services/api';
import api from '../services/api';
import { APP_VERSION } from '../version';
import {
  LayoutDashboard, Users, Briefcase, ShoppingCart,
  Calendar, Mail, BarChart3, Settings, LogOut,
  Menu, X, ChevronDown, FolderOpen, Building2, Check,
  Bell, Shield, MailSearch, Search, Plus, BellRing, ListChecks, TrendingUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const navGroups = [
  {
    label: 'Main',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'staff', 'trade'] },
      { name: 'Action List', href: '/actions', icon: ListChecks, roles: ['admin', 'staff'], ai: true },
      { name: 'Jobs', href: '/jobs', icon: Briefcase, roles: ['admin', 'staff', 'trade'] },
      { name: 'PO Inbox', href: '/po-inbox', icon: MailSearch, roles: ['admin', 'staff'], ai: true },
      { name: 'Calendar', href: '/calendar', icon: Calendar, roles: ['admin', 'staff', 'trade'] },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { name: 'Purchase Orders', href: '/purchase-orders', icon: ShoppingCart, roles: ['admin', 'staff'] },
      { name: 'Schedule of Rates', href: '/schedule-of-rates', icon: TrendingUp, roles: ['admin', 'staff'], ai: true },
      { name: 'Suppliers', href: '/suppliers', icon: Building2, roles: ['admin', 'staff'] },
      { name: 'Trades', href: '/contractors', icon: Users, roles: ['admin', 'staff'] },
      { name: 'Documents', href: '/documents', icon: FolderOpen, roles: ['admin', 'staff'] },
    ],
  },
  {
    label: 'Insights',
    items: [
      { name: 'Emails', href: '/emails', icon: Mail, roles: ['admin', 'staff'] },
      { name: 'Reports', href: '/reports', icon: BarChart3, roles: ['admin', 'staff'] },
      { name: 'Reminders', href: '/reminders', icon: BellRing, roles: ['admin', 'staff'], ai: true },
      { name: 'Notifications', href: '/notifications', icon: Bell, roles: ['admin', 'staff', 'trade'] },
    ],
  },
];

const allItems = navGroups.flatMap((g) => g.items);

const defaultBranding = { company_name: 'My Project Agent', logo_url: null };

export const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [branding, setBranding] = useState(defaultBranding);
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [actionCount, setActionCount] = useState(0);

  useEffect(() => {
    if (!user || !['admin', 'staff'].includes(user.role)) return;
    let active = true;
    const fetchActions = async () => {
      try {
        const res = await api.get('/action-list');
        if (active) setActionCount(res.data?.counts?.total || 0);
      } catch (e) { /* ignore */ }
    };
    fetchActions();
    const t = setInterval(fetchActions, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user, location.pathname]);

  useEffect(() => {
    const checkSuperuser = async () => {
      try {
        const res = await api.get('/admin/is-superuser');
        setIsSuperuser(res.data.is_superuser);
      } catch (error) {
        setIsSuperuser(false);
      }
    };
    if (user) checkSuperuser();
  }, [user]);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await brandingAPI.get();
        if (response.data) setBranding({ ...defaultBranding, ...response.data });
      } catch (error) {
        console.log('Using default branding');
      }
    };
    fetchBranding();
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await companiesAPI.getAll();
        setCompanies(response.data || []);
        if (response.data?.length > 0) {
          const active = response.data.find((c) => c.company?.id === user?.active_company_id) || response.data[0];
          setActiveCompany(active);
        }
      } catch (error) {
        console.log('Failed to fetch companies');
      }
    };
    if (user) fetchCompanies();
  }, [user]);

  const handleSwitchCompany = async (companyData) => {
    try {
      await companiesAPI.switch(companyData.company.id);
      setActiveCompany(companyData);
      toast.success(`Switched to ${companyData.company.name}`);
      window.location.reload();
    } catch (error) {
      toast.error('Failed to switch company');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleOf = user?.role || 'staff';
  const currentItem = allItems.find((i) => location.pathname.startsWith(i.href));
  const sectionTitle = location.pathname.startsWith('/settings')
    ? 'Settings'
    : location.pathname.startsWith('/admin')
    ? 'Platform Admin'
    : currentItem?.name || 'Dashboard';

  return (
    <div className="h-screen overflow-hidden bg-grid p-2.5 sm:p-3 flex gap-3">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Floating sidebar */}
      <aside
        className={`fixed inset-y-3 left-2.5 sm:left-3 z-50 w-[16.5rem] surface flex flex-col transition-transform duration-300 lg:static lg:inset-auto lg:z-auto ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-[115%]'
        } lg:translate-x-0`}
      >
        {/* Brand */}
        <div className="flex items-center justify-between h-[4.25rem] px-4 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl brand-gradient flex items-center justify-center shadow-glow">
              <Briefcase className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight font-heading">
              My Project Agent
            </span>
          </Link>
          <button className="lg:hidden text-muted-foreground" onClick={() => setSidebarOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Grouped nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {navGroups.map((group) => {
            const items = group.items.filter((i) => i.roles.includes(roleOf));
            if (items.length === 0) return null;
            return (
              <div key={group.label}>
                <p className="px-2.5 mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                  {group.label}
                </p>
                <div className="space-y-1">
                  {items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href || (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`group flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                          isActive
                            ? 'brand-gradient text-white shadow-glow'
                            : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
                        }`}
                      >
                        <span className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isActive ? 'bg-white/20' : 'bg-secondary/70 group-hover:bg-secondary'}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="flex-1">{item.name}</span>
                        {item.href === '/actions' && actionCount > 0 ? (
                          <span
                            className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center text-[11px] font-bold rounded-full ${isActive ? 'bg-white text-primary' : 'bg-red-500 text-white'}`}
                            data-testid="nav-action-count"
                          >
                            {actionCount > 99 ? '99+' : actionCount}
                          </span>
                        ) : item.ai && (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${isActive ? 'bg-white/25 text-white' : 'bg-primary/15 text-primary'}`}>AI</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {isSuperuser && (
            <div>
              <p className="px-2.5 mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">Platform</p>
              <Link
                to="/admin"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm font-medium ${
                  location.pathname === '/admin' ? 'bg-amber-500/20 text-amber-300' : 'text-amber-400 hover:bg-amber-500/10'
                }`}
              >
                <span className="h-8 w-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
                  <Shield className="h-4 w-4" />
                </span>
                Platform Admin
              </Link>
            </div>
          )}
        </nav>

        {/* User card + settings */}
        <div className="p-3 border-t border-border space-y-2">
          {roleOf === 'admin' && (
            <Link
              to="/settings"
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-settings"
              className={`flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm font-medium transition-colors ${
                location.pathname === '/settings' ? 'brand-gradient text-white' : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
              }`}
            >
              <span className="h-8 w-8 rounded-lg bg-secondary/70 flex items-center justify-center"><Settings className="h-4 w-4" /></span>
              Settings
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 px-2.5 py-2 rounded-xl hover:bg-secondary/60 transition-colors" data-testid="user-menu-btn">
                <div className="h-9 w-9 rounded-full brand-gradient flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-white">{user?.name?.charAt(0).toUpperCase() || 'U'}</span>
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize truncate">{user?.role}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56 bg-card border-border mb-1">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <p className="text-[10px] text-muted-foreground/40 text-center pt-1">{APP_VERSION}</p>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 gap-3">
        {/* Floating top bar */}
        <header className="surface h-[4.25rem] flex items-center gap-3 px-3 sm:px-4 flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-foreground" data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6" />
          </button>

          <div className="min-w-0">
            <p className="text-[11px] text-muted-foreground leading-none">{branding.company_name}</p>
            <h1 className="text-lg sm:text-xl font-bold font-heading tracking-tight truncate">{sectionTitle}</h1>
          </div>

          {/* Search (decorative command bar) */}
          <div className="hidden md:flex items-center gap-2 ml-6 flex-1 max-w-md">
            <div className="flex items-center gap-2 w-full h-9 px-3 rounded-xl bg-input/60 border border-border text-muted-foreground">
              <Search className="h-4 w-4" />
              <span className="text-sm">Search jobs, POs, suppliers…</span>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {roleOf !== 'trade' && (
              <Button size="sm" className="hidden sm:flex" onClick={() => navigate('/jobs')} data-testid="topbar-new-job">
                <Plus className="h-4 w-4 mr-1" /> New Job
              </Button>
            )}

            {companies.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="hidden md:flex items-center gap-2" data-testid="company-switcher">
                    <Building2 className="h-4 w-4" />
                    <span className="max-w-[130px] truncate">{activeCompany?.company?.name || 'Company'}</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 bg-card border-border">
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-xs text-muted-foreground">Switch Company</p>
                  </div>
                  {companies.map((c) => (
                    <DropdownMenuItem key={c.company?.id} onClick={() => handleSwitchCompany(c)} className="cursor-pointer flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{c.company?.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{c.role}</p>
                        </div>
                      </div>
                      {activeCompany?.company?.id === c.company?.id && <Check className="h-4 w-4 text-primary" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Link to="/notifications" className="h-9 w-9 rounded-xl bg-secondary/60 hover:bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" data-testid="topbar-bell">
              <Bell className="h-4 w-4" />
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto pr-0.5">
          <div className="pb-6">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
